/**
 * When a follower deposes a state leader that stopped answering it.
 *
 * The discovery server elects the state leader (localDiscovery
 * leaderElection.mjs): the first member of the `state-leader` list whose last
 * pong reported an open WebRTC channel. It removes a member that stops
 * ponging, but a leader that pongs and holds one channel to anybody stays
 * leader however many followers it fails. Seen at the Simtable office on
 * 2026-10-21: a CameraMap tab led `geocamera` for hours while every GeoCamera
 * logged "leader unresponsive" and nagged it with resync requests nobody
 * answered.
 *
 * A follower now sends the same `capabilityDepose` that GroupExplorer's
 * Depose button sends, when all of these hold:
 *  - the server's list is in hand, names the leader, and has another member
 *    to take over;
 *  - the leader has been silent toward this follower for DEPOSE_AFTER_MS
 *    since it became leader or last answered;
 *  - at least DEPOSE_MIN_REQUESTS resync requests went unanswered (a healthy
 *    leader answers each with full state, so a quiet group never trips this);
 *  - this follower has an open channel to some peer other than the leader, so
 *    a page whose own transport is broken (every channel failing) cannot
 *    depose good leaders;
 *  - this follower has not deposed anyone in the last DEPOSE_COOLDOWN_MS.
 *
 * The server then does what it does for the button: drops the leader from
 * the list and rebroadcasts; every page applies the next leader, and the
 * deposed page rejoins at the end of the line.
 */

export const DEPOSE_AFTER_MS = 90_000
export const DEPOSE_MIN_REQUESTS = 3
export const DEPOSE_COOLDOWN_MS = 10 * 60_000

/**
 * @param {object} s
 * @param {number} s.now
 * @param {string|null} s.leader             the leader this page follows
 * @param {string} s.self                    this page's instance id
 * @param {Array<{instanceId: string}>|null} s.members  the server's list (null: none yet)
 * @param {number} s.leaderSince             when this page applied the leader
 * @param {number} s.lastLeaderResponse      last state message from the leader (0: none)
 * @param {number} s.unansweredResyncs       resync requests sent since the leader last answered
 * @param {number} s.openChannelsElsewhere   open channels to peers other than the leader
 * @param {number} s.lastDeposeAt            when this page last deposed a leader (0: never)
 * @returns {{ depose: boolean, reason: string, silentMs?: number }}
 */
export function shouldDeposeLeader(s) {
    const no = (reason) => ({ depose: false, reason })
    if (!s.leader || s.leader === s.self) return no('not following a leader')
    if (!Array.isArray(s.members)) return no('no server list')
    if (!s.members.some((m) => m.instanceId === s.leader)) return no('leader not in the server list')
    if (!s.members.some((m) => m.instanceId !== s.leader)) return no('no other member to take over')
    const silentMs = s.now - Math.max(s.lastLeaderResponse || 0, s.leaderSince || 0)
    if (silentMs < DEPOSE_AFTER_MS) return no('leader not silent long enough')
    if ((s.unansweredResyncs || 0) < DEPOSE_MIN_REQUESTS) return no('too few unanswered resync requests')
    if (!(s.openChannelsElsewhere > 0)) return no('no open channel here: this page may be the broken one')
    if (s.lastDeposeAt && s.now - s.lastDeposeAt < DEPOSE_COOLDOWN_MS) return no('deposed one recently')
    return { depose: true, reason: 'leader silent', silentMs }
}
